import React, { useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Hexagon,
  Leaf,
  ShoppingBagOpen,
  ShoppingCart,
  Storefront,
} from "phosphor-react";
import appLogo from "assets/logo/logo-only.png";
import usePremium from "hooks/usePremium";
import Button from "components/Button";
import UploadFile from "components/Modal/UploadFile";
import Dropdown from "components/Dropdown";
import CartModal from "components/Modal/CartModal";
import Divider from "components/elements/Divider";
import AddFarm from "../Modal/AddFarm";
import "./index.scss";

const Navbar = () => {
  const history = useHistory();
  const { isPremium } = usePremium();
  const { user, logout } = useAuth0();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [farmName, setFarmName] = useState("");
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openCartModal, setOpenCartModal] = useState(false);

  const createNewFarm = () => {
    setIsOpenModal(true);
    setFarmName("");
  };

  const closeModal = () => {
    setIsOpenModal(false);
    setOpenUploadFile(false);
    setFarmName("");
  };

  const handleChangeStatus = () => {};
  const addDropdownData = [
    {
      id: "new-farm",
      label: "Create New Farm",
      handleClick: () => createNewFarm(),
    },
    {
      id: "order-imagery",
      label: "Order High-Resolution Imagery",
      handleClick: () => history.push("/order/high-resolution-imagery"),
    },
    {
      id: "rapid-em",
      label: "Book Rapid-EM™",
      handleClick: () => history.push("/rapid-em-booking"),
    },
  ];
  const profileDropdownData = [
    {
      id: "profile",
      label: "Profile Settings",
      handleClick: () => history.push("/settings#profile"),
    },
    // For Future Implementation
    // {
    //   id: "subscription",
    //   label: "Manage Subscription",
    //   handleClick: () => history.push("/settings#subscription-status"),
    // },
    {
      id: "delete",
      label: "Log Out",
      handleClick: () => logout({ returnTo: window.location.origin }),
    },
  ];

  return (
    <>
      <header className="page-header" data-testid="page-header">
        <nav
          className={`nav-bar ${mobileNavOpen ? "-fixed" : ""}`}
          data-testid="nav-bar"
        >
          {mobileNavOpen ? (
            <Button
              iconOnly
              icon="close"
              type="outlined"
              color="white"
              onClick={() => setMobileNavOpen(false)}
            />
          ) : (
            <Button
              iconOnly
              icon="menu"
              type="outlined"
              color="#131B34"
              onClick={() => setMobileNavOpen(true)}
            />
          )}
          <img
            data-testid="app-logo"
            className="app-logo"
            src={appLogo}
            alt="alt"
            onClick={() => history.push("/")}
            aria-hidden
          />
          <div className="nav-menu">
            <NavLink
              to="/dashboard"
              activeClassName="-active"
              className="link"
              data-testid="dashboard"
            >
              <Hexagon size={24} weight="fill" />
              Home
            </NavLink>
            <NavLink
              to="/farms"
              activeClassName="-active"
              className="link"
              data-testid="farms-and-fields"
            >
              <Leaf size={24} />
              Farms
            </NavLink>
            <NavLink
              to="/order"
              activeClassName="-active"
              className="link"
              data-testid="orders"
            >
              <ShoppingBagOpen size={24} />
              Orders
            </NavLink>
            <NavLink
              to="/store"
              activeClassName="-active"
              className="link"
              data-testid="store"
            >
              <Storefront size={24} />
              Store
            </NavLink>
          </div>
          <div className="actions-container">
            {isPremium ? (
              <NavLink
                to="/advanced"
                activeClassName="-active"
                className="premium-badge"
                data-testid="premium"
              >
                You&apos;re using Advanced
              </NavLink>
            ) : (
              <NavLink
                to="/advanced"
                activeClassName="-active"
                className="premium"
                data-testid="premium"
              >
                Get Advanced
              </NavLink>
            )}
            <Dropdown
              icon="add"
              data={addDropdownData}
              iconOnly
              data-testid="add-dropdown"
              buttonColor="#131B34"
            />
            <span onClick={() => setOpenCartModal(true)} aria-hidden>
              <ShoppingCart size={24} color="white" />
            </span>
            <Dropdown
              className="profile-avatar"
              data={profileDropdownData}
              avatar
              avatarName={user.nickname}
              data-testid="profile-dropdown"
              size={38}
            />
          </div>
        </nav>
        <div className={`mobile-nav ${mobileNavOpen ? "-show" : "-hidden"}`}>
          <NavLink
            to="/dashboard"
            activeClassName="-active"
            className="link"
            data-testid="mobile-dashboard"
          >
            Home
          </NavLink>
          <NavLink
            to="/farms"
            activeClassName="-active"
            className="link"
            data-testid="mobile-farms-and-fields"
          >
            Farms
          </NavLink>
          <NavLink
            to="/order"
            activeClassName="-active"
            className="link"
            data-testid="mobile-orders"
          >
            Orders
          </NavLink>
          <NavLink
            to="/store"
            activeClassName="-active"
            className="link"
            data-testid="mobile-store"
          >
            Store
          </NavLink>
          <Divider />
          <NavLink
            to="/settings#profile"
            activeClassName="-active"
            className="link"
          >
            Profile Settings
          </NavLink>
          <div
            activeClassName="-active"
            className="link"
            onClick={() => logout({ returnTo: window.location.origin })}
            aria-hidden
          >
            Logout
          </div>
        </div>
      </header>
      <AddFarm
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        onClose={closeModal}
        setFarmDetails={setFarmName}
        buttonAction={() => setOpenUploadFile(true)}
      />
      {openCartModal && (
        <CartModal
          isOpen={openCartModal}
          onClose={() => setOpenCartModal(false)}
        />
      )}
      {openUploadFile ? (
        <UploadFile
          isOpen={openUploadFile}
          onRequestClose={closeModal}
          onClose={closeModal}
          farmName={farmName}
          show="New"
          handleChangeStatus={() => handleChangeStatus}
        />
      ) : null}
    </>
  );
};

export default Navbar;
